import { useMutation, useQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import {
	Button,
	ColorPicker,
	DatePicker,
	DeleteIcon,
	Input,
	LockIcon,
	Modal,
	Select,
	Text,
	TextArea,
} from '@viapeople/components'
import MarkdownEditor from '@viapeople/components/src/components/MarkdownEditor'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { trpc } from '../../../utils/trpc'
import type { FileUploadRequestInput } from '../../organization/types'
import { ExternalDataSourceSelectorEditor } from '../components/formComponents/ExternalDataSourceSelectorEditor'
import { ConditionalConfigEditor } from '../components/propertyEditors/ConditionalConfigEditor'
import { DataSourceConfigEditor } from '../components/propertyEditors/DataSourceConfigEditor'
import { useFormBuilderStore } from '../store'
import { componentRegistry } from '../store/componentRegistry'

import type {
	FormInputComponent,
	PropertyEditorProps,
	PropertyEditorStrategy,
	PropertyEditorStrategyMap,
} from '../types'

class TextPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
		return (
			<div key={name}>
				<Text variant='small' className='block mb-2 text-left'>
					{label}
				</Text>
				<Input
					value={(value as string) || ''}
					onChange={(e) => onChange(e.target.value)}
					placeholder={`Enter ${label?.toLowerCase()}`}
				/>
			</div>
		)
	}
}

class TextAreaPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
		return (
			<div key={name}>
				<Text variant='small' className='block mb-2 text-left'>
					{label}
				</Text>
				<TextArea value={(value as string) || ''} onChange={(e) => onChange(e.target.value)} rows={6} />
			</div>
		)
	}
}

class RichTextAreaPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
		return (
			<div key={name}>
				<Text variant='small' className='block mb-2 text-left'>
					{label}
				</Text>

				<MarkdownEditor
					defaultValue={value != null ? String(value) : undefined}
					onChangeMarkdown={(markdown) => {
						onChange(markdown)
					}}
					rows={6}
				/>
			</div>
		)
	}
}

class ColorPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
		return (
			<div key={name}>
				{label && (
					<Text variant='small' className='block mb-2 text-left'>
						{label}
					</Text>
				)}
				<ColorPicker value={(value as string) || '#000000'} onChange={(val) => onChange(val)} placeholder='#000000' />
			</div>
		)
	}
}

class SelectPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent({
		name,
		label,
		value,
		onChange,
		options = [],
		referenceContext,
	}: PropertyEditorProps): React.ReactElement {
		if (name === 'permissionTemplateId') {
			return <PermissionTemplateSelect name={name} label={label} value={value} onChange={onChange} />
		}
		if (name === 'notifyEmailTemplateId') {
			return (
				<EmailTemplateSelect
					name={name}
					label={label}
					value={value}
					onChange={onChange}
					referenceContext={referenceContext}
				/>
			)
		}
		return (
			<div key={name}>
				<Text variant='small' className='block mb-2 text-left'>
					{label}
				</Text>
				<Select value={String(value)} onChange={(val) => onChange(val)} options={options} placeholder={'Select'} />
			</div>
		)
	}
}

function EmailTemplateSelect({ name, label, value, onChange, referenceContext }: PropertyEditorProps) {
	// referenceContext is notifyHrOnFlag's value (forwarded by DynamicPropertyEditor
	// via editorReferenceId in the registry). Hide the select entirely when notify
	// is off — picking a template that won't be used would only confuse the admin.
	const notifyEnabled = referenceContext === true || referenceContext === 'true'

	const { organizationId } = useParams<{ organizationId: string }>()

	const { data, isLoading } = useQuery({
		...trpc.admin.emailTemplates.search.queryOptions({
			organizationId: organizationId as string,
			search: '',
			perPage: 200,
			page: 1,
		}),
		// Skip the network call when the parent toggle is off — the hook still
		// runs on every render (rules-of-hooks), but no request fires.
		enabled: !!organizationId && notifyEnabled,
	})

	const templateOptions = React.useMemo(() => {
		const items = data?.data ?? []
		return [
			{ value: '', label: 'No template selected' },
			...items.map((template: { id: string; name: string }) => ({
				value: template.id,
				label: template.name,
			})),
		]
	}, [data])

	if (!notifyEnabled) return null

	return (
		<div key={name}>
			<Text variant='small' className='block mb-2 text-left'>
				{label}
			</Text>
			<Select
				value={(value as string) || ''}
				onChange={(val) => onChange(val)}
				options={templateOptions}
				placeholder={isLoading ? 'Loading…' : 'Select email template'}
			/>
		</div>
	)
}

class DefaultValueSelectorPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent({
		name,
		label,
		value,
		onChange,
		defaultValue,
		referenceContext,
	}: PropertyEditorProps): React.ReactElement {
		const options = (referenceContext as Array<{ value: string; label: string }>) || []
		const EMPTY_SELECTION = {
			value: '',
			label: 'None',
		}

		return (
			<div key={name}>
				<Text variant='small' className='block mb-2 text-left'>
					{label}
				</Text>
				<Select
					defaultValue={defaultValue as string}
					value={String(value)}
					onChange={(val) => onChange(val)}
					options={[
						EMPTY_SELECTION,
						...options.map((opt) => ({
							value: opt.value ? opt.value : opt.label,
							label: opt.label,
						})),
					]}
					placeholder={'Select'}
				/>
			</div>
		)
	}
}

function PermissionTemplateSelect({ name, label, value, onChange }: PropertyEditorProps) {
	const { organizationId } = useParams<{ organizationId: string }>()

	const { data, isLoading } = useQuery({
		...trpc.admin.permissionTemplates.search.queryOptions({
			organizationId: organizationId as string,
			search: '',
			perPage: 100,
			page: 1,
		}),
		enabled: !!organizationId,
	})

	const templateOptions = React.useMemo(() => {
		if (!data?.data) return []

		return data.data.map((template) => ({
			value: template.id,
			label: template.name,
		}))
	}, [data])

	// Auto-select the first permission template if no value is set
	useEffect(() => {
		if (!value && templateOptions.length > 0 && !isLoading) {
			onChange(templateOptions[0].value)
		}
	}, [value, templateOptions, isLoading, onChange])

	return (
		<div key={name}>
			<Text variant='small' className='block mb-2 text-left'>
				{label}
			</Text>
			<Select
				value={(value as string) || ''}
				onChange={(val) => onChange(val)}
				options={templateOptions}
				placeholder={isLoading ? 'Loading...' : 'Select'}
			/>
			{isLoading && (
				<Text variant='small' className='mt-1 text-neutral-500'>
					Loading templates...
				</Text>
			)}
		</div>
	)
}

class NumberPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent({ name, label, value, onChange, options }: PropertyEditorProps): React.ReactElement {
		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value
			if (value === '') {
				onChange(undefined)
			} else {
				onChange(Number(value))
			}
		}
		const maxLimit = options?.find((opt) => opt.label === 'maxLimit')?.value
		return (
			<div key={name}>
				<Text variant='small' className='block mb-2 text-left'>
					{label}
				</Text>
				<Input
					type='number'
					value={value?.toString() || ''}
					onChange={handleChange}
					min={0}
					max={maxLimit}
					placeholder={`Enter ${label?.toLowerCase()}`}
				/>
			</div>
		)
	}
}

const defaultTrueProperties = ['showResponseAverage']

class BooleanPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
		const isChecked = defaultTrueProperties.includes(name)
			? value === undefined
				? true
				: Boolean(value)
			: Boolean(value)

		return (
			<div key={name} className='flex items-center'>
				<input
					type='checkbox'
					id={name}
					checked={isChecked}
					onChange={(e) => onChange(e.target.checked)}
					className='mr-2'
				/>
				<label htmlFor={name}>
					<Text variant='small' className='text-left'>
						{label}
					</Text>
				</label>
			</div>
		)
	}
}

interface RatingOption {
	value: string
	valueLabel: string
	labelColor?: string
}
type RatingPropertyValue = RatingOption[] | string | number | boolean | null
interface RatingPropertyEditorProps extends PropertyEditorProps {
	value: RatingPropertyValue
	onChange: (value: RatingPropertyValue) => void
}
class RatingPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent({ name, label, value, onChange, options = [] }: RatingPropertyEditorProps): React.ReactElement {
		const contextOption = options.find((opt) => opt.value === 'context')
		const componentProps = contextOption?.context || {}
		const count = Number(componentProps.count) || 5

		switch (name) {
			case 'count':
				return this.renderCountEditor({ name, label, value, onChange })
			case 'defaultValue':
				return this.renderDefaultValueEditor({ name, label, value, onChange, count })
			case 'options': {
				const isOptions = (value: unknown): value is RatingOption[] => {
					return (
						Array.isArray(value) &&
						value.length > 0 &&
						typeof value[0] === 'object' &&
						value[0] !== null &&
						'value' in value[0] &&
						'valueLabel' in value[0]
					)
				}

				if (!isOptions(value)) {
					throw new Error('invalid value for rating options')
				}

				return this.renderOptionsEditor({ name, value, onChange, count })
			}
			default:
				return propertyEditorStrategies.text.getEditorComponent({
					name,
					label,
					value,
					onChange,
					options,
				})
		}
	}

	private renderCountEditor({
		name,
		label,
		value,
		onChange,
	}: Omit<PropertyEditorProps, 'options'>): React.ReactElement {
		return (
			<div key={name}>
				<Text variant='small' className='block mb-2 text-left'>
					{label}
				</Text>
				<Input
					value={Number(value || 5)}
					onChange={(event) => onChange(Number(event.target.value))}
					placeholder='Options length'
					type='number'
				/>
			</div>
		)
	}

	private renderDefaultValueEditor({
		name,
		label,
		value,
		onChange,
		count,
	}: Omit<PropertyEditorProps, 'options'> & { count: number }): React.ReactElement {
		const dynamicOptions = [
			{ value: '', label: 'None' },
			...Array.from({ length: count }, (_, i) => ({
				value: String(i + 1),
				label: String(i + 1),
			})),
		]

		return (
			<div key={name}>
				<Text variant='small' className='block mb-2 text-left'>
					{label}
				</Text>
				<Select
					value={String(value || '')}
					onChange={(val) => onChange(val === '' ? '' : Number(val))}
					options={dynamicOptions}
					placeholder='Select'
				/>
			</div>
		)
	}

	private renderOptionsEditor({
		name,
		value,
		onChange,
		count,
	}: {
		name: string
		value: RatingOption[]
		onChange: (value: RatingOption[]) => void
		count: number
	}): React.ReactElement {
		let currentOptions: RatingOption[] = []
		if (Array.isArray(value)) {
			currentOptions = [...value]
		}

		for (let j = 0; j < count; j++) {
			if (!currentOptions[j]) {
				currentOptions[j] = {
					value: String(j + 1),
					valueLabel: String(j + 1),
					labelColor: '#2563EB',
				}
			}
		}

		return <RatingOptionsEditor name={name} options={currentOptions} count={count} onChange={onChange} />
	}
}

function RatingOptionsEditor({
	name,
	options,
	count,
	onChange,
}: {
	name: string
	options: RatingOption[]
	count: number
	onChange: (value: RatingOption[]) => void
}) {
	const [selectedRatingIndex, setSelectedRatingIndex] = React.useState(0)
	const prevCountRef = React.useRef(count)

	React.useEffect(() => {
		if (count !== prevCountRef.current) {
			const updatedOptions = [...options]
			for (let j = 0; j < count; j++) {
				if (!updatedOptions[j]) {
					updatedOptions[j] = {
						value: String(j + 1),
						valueLabel: String(j + 1),
						labelColor: '#2563EB',
					}
				} else if (!updatedOptions[j].labelColor) {
					updatedOptions[j].labelColor = '#2563EB'
				}
			}
			onChange(updatedOptions)
			prevCountRef.current = count
		}
	}, [count, onChange, options])

	const ratingSelectOptions = Array.from({ length: count }, (_, i) => ({
		value: String(i),
		label: `${i + 1}`,
	}))

	const updateRatingOption = (key: string, val: string) => {
		const newOptions = [...options]

		newOptions[selectedRatingIndex] = {
			...(newOptions[selectedRatingIndex] || {}),
			[key]: val,
		}
		onChange(newOptions)
	}

	const selectedRating = options[selectedRatingIndex] || {
		value: String(selectedRatingIndex + 1),
		valueLabel: String(selectedRatingIndex + 1),
		labelColor: '#2563EB',
	}

	const displayRating = {
		...selectedRating,
		labelColor: selectedRating.labelColor || '#2563EB',
	}

	return (
		<div key={name}>
			<div className='space-y-4'>
				<div>
					<Text variant='small' className='block mb-2 text-left'>
						Select Rating to Edit
					</Text>
					<Select
						value={String(selectedRatingIndex)}
						onChange={(val) => setSelectedRatingIndex(Number(val))}
						options={ratingSelectOptions}
						placeholder='Select a rating to edit'
					/>
				</div>

				<div className='space-y-3 p-3 border border-neutral-200 rounded-md bg-neutral-50'>
					<div>
						<Text variant='small' className='block mb-1 text-left'>
							Rating Value
						</Text>
						<Input
							value={displayRating.value}
							onChange={(e) => updateRatingOption('value', e.target.value)}
							placeholder='Enter value'
							type='text'
							maxLength={3}
						/>
					</div>

					<div>
						<Text variant='small' className='block mb-1 text-left'>
							Value Label
						</Text>
						<Input
							value={displayRating.valueLabel}
							onChange={(e) => updateRatingOption('valueLabel', e.target.value)}
							placeholder='Enter label'
						/>
					</div>

					<div>
						<Text variant='small' className='block mb-1 text-left'>
							Label & Selected Color
						</Text>
						<ColorPicker
							value={displayRating.labelColor}
							onChange={(val) => updateRatingOption('labelColor', val)}
							placeholder='#2563EB'
						/>
					</div>

					<div className='mt-2 p-2 rounded border border-neutral-200' style={{ backgroundColor: '#f9fafb' }}>
						<Text variant='small' weight='semibold' style={{ color: displayRating.labelColor }}>
							Preview: {selectedRating.valueLabel}
						</Text>
					</div>
				</div>
			</div>
		</div>
	)
}

class OptionsPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
		interface DropdownOption {
			label: string
			value: string | number
		}

		let currentOptions: DropdownOption[] = []
		if (Array.isArray(value)) {
			currentOptions = value as DropdownOption[]
		}

		const addOption = () => {
			const newOptions = [...currentOptions, { label: '', value: '' }]
			onChange(newOptions)
		}

		const removeOption = (index: number) => {
			const newOptions = currentOptions.filter((_, i) => i !== index)
			onChange(newOptions)
		}

		const updateOption = (index: number, field: 'label' | 'value', newValue: string) => {
			const newOptions = [...currentOptions]
			newOptions[index] = { ...newOptions[index], [field]: newValue }

			onChange(newOptions)
		}

		return (
			<div key={name}>
				<div className='flex items-center justify-between mb-2'>
					<Text variant='small' className='text-left'>
						{label}
					</Text>
					<Button onClick={addOption} variant={'tertiary'} size='small'>
						+
					</Button>
				</div>

				<div className='space-y-2 max-h-60 overflow-y-auto'>
					{currentOptions.length === 0 ? (
						<div className='text-center py-4 text-neutral-500 border-2 border-dashed border-neutral-300 rounded-md'>
							<Text variant='small'>No options added yet</Text>
						</div>
					) : (
						currentOptions.map((option, index) => (
							<div
								key={`${option.value}-${option.label}`}
								className='flex gap-2 items-center p-2 border border-neutral-200 rounded'>
								<div className='flex-1'>
									<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
										Label
									</Text>
									<Input
										value={option.label}
										onChange={(e) => updateOption(index, 'label', e.target.value)}
										placeholder='Display text'
										className='text-sm'
									/>
								</div>
								<div className='flex-1'>
									<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
										Value
									</Text>
									<Input
										value={option.value.toString()}
										onChange={(e) => {
											const val = e.target.value
											// Try to convert to number if it's numeric, otherwise keep as string
											const finalValue = !Number.isNaN(Number(val)) && val !== '' ? Number(val) : val
											updateOption(index, 'value', finalValue.toString())
										}}
										placeholder='Data value'
										className='text-sm'
									/>
								</div>
								<button
									type='button'
									onClick={() => removeOption(index)}
									className='text-red-500 hover:text-red-700 p-1'
									title='Remove option'>
									×
								</button>
							</div>
						))
					)}
				</div>
			</div>
		)
	}
}

class DividerPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent(_props: PropertyEditorProps): React.ReactElement {
		return <div className='h-px bg-neutral-200 my-4' />
	}
}

class TextLimitPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent(props: PropertyEditorProps): React.ReactElement {
		return <TextLimitPropertyEditor {...props} />
	}
}

function TextLimitPropertyEditor({
	name,
	label,
	onChange,
	options,
	defaultValue,
}: PropertyEditorProps): React.ReactElement {
	const [isWordLimit, setIsWordLimit] = useState(true)
	const defaultValues = defaultValue as unknown as {
		textLimit: { isWordLimit: boolean; characters: number; words: number }
	}
	const [wordLimit, setWordLimit] = useState(defaultValues?.textLimit?.words || 500)
	const [charLimit, setCharLimit] = useState(defaultValues?.textLimit?.characters || 3000)
	const wordsLabel = options?.find((opt) => opt.value === 'words')?.label
	const charactersLabel = options?.find((opt) => opt.value === 'characters')?.label

	useEffect(() => {
		if (defaultValue) {
			const defaultValues = defaultValue as unknown as {
				textLimit: { isWordLimit: boolean; characters: number; words: number }
			}
			if (defaultValues?.textLimit) {
				setIsWordLimit(defaultValues.textLimit.isWordLimit)
				setWordLimit(defaultValues.textLimit.words)
				setCharLimit(defaultValues.textLimit.characters)
			}
		}
	}, [defaultValue])

	const handleToggleChange = (checked: boolean) => {
		setIsWordLimit(checked)
		onChange({
			isWordLimit: checked,
			characters: charLimit,
			words: wordLimit,
		})
	}

	const handleWordLimitChange = (newValue: string) => {
		const numValue = Number.parseInt(newValue, 10) || 0
		setWordLimit(numValue)
		onChange({
			isWordLimit,
			characters: charLimit,
			words: numValue,
		})
	}

	const handleCharLimitChange = (newValue: string) => {
		const numValue = Number.parseInt(newValue, 10) || 0
		setCharLimit(numValue)
		onChange({
			isWordLimit,
			characters: numValue,
			words: wordLimit,
		})
	}

	return (
		<div key={name} className='space-y-4'>
			<Text variant='small' className='block mb-2 text-left'>
				{label}
			</Text>

			<div className='flex items-center space-x-3'>
				<label className='inline-flex items-center cursor-pointer'>
					<input
						type='checkbox'
						className='sr-only peer'
						checked={isWordLimit}
						onChange={(e) => handleToggleChange(e.target.checked)}
					/>
					<div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 peer-focus:ring-primary-600 dark:peer-focus:ring-neutrals-300 rounded-full peer dark:bg-neutrals-200 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-neutrals-0 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
				</label>
				<Text variant='xsmall' className='text-neutral-600'>
					{isWordLimit ? 'Word Limit' : 'Character Limit'}
				</Text>
			</div>

			<div className='grid grid-cols-2 gap-4'>
				{isWordLimit && (
					<div>
						<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
							{wordsLabel}
						</Text>
						<Input
							type='number'
							value={wordLimit}
							onChange={(e) => handleWordLimitChange(e.target.value)}
							placeholder='500'
							min={1}
							max={10000}
						/>
					</div>
				)}
				{!isWordLimit && (
					<div>
						<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
							{charactersLabel}
						</Text>
						<Input
							type='number'
							value={charLimit}
							onChange={(e) => handleCharLimitChange(e.target.value)}
							placeholder='3000'
							min={1}
							max={100000}
						/>
					</div>
				)}
			</div>
		</div>
	)
}

function RatingComponentSelectorEditor({
	name,
	label,
	value,
	onChange,
	options = [],
}: PropertyEditorProps): React.ReactElement {
	const formComponents = useFormBuilderStore((state) => state.formComponents)
	const selectedComponentId = useFormBuilderStore((state) => state.selectedComponentId)
	const findParentComponent = useFormBuilderStore((state) => state.findParentComponent)

	// Extract context from options
	const contextOption = options.find((opt) => opt.value === 'context')
	const componentProps = (contextOption?.context || {}) as Record<string, unknown>
	const averageSection = Boolean(componentProps.averageSection)

	interface AvailableComponent {
		id: string
		label: string
		name: string
	}

	// Get components in the current section if averageSection is enabled
	const getSectionComponents = React.useCallback((): AvailableComponent[] => {
		if (!averageSection || !selectedComponentId) {
			return []
		}

		// Find the parent section
		const { parent } = findParentComponent(selectedComponentId)

		// If there's no parent, this average component is at the root level
		if (!parent) {
			return []
		}

		// Check if the parent is a section
		const parentCustom = parent.custom as { isSection?: boolean }
		if (!parentCustom?.isSection) {
			return []
		}

		// Collect all rating/number/select components within this section
		const components: AvailableComponent[] = []
		const collectComponents = (comps: typeof formComponents) => {
			comps.forEach((comp) => {
				// Don't include the average component itself
				if (
					comp.id !== selectedComponentId &&
					(comp.name === 'rating' || comp.name === 'number' || comp.name === 'select')
				) {
					components.push({
						id: comp.id,
						label: comp.label || (comp.custom as { label?: string })?.label || comp.name,
						name: comp.name,
					})
				}
				if (comp.children && comp.children.length > 0) {
					collectComponents(comp.children)
				}
			})
		}

		if (parent.children) {
			collectComponents(parent.children)
		}

		return components
	}, [averageSection, selectedComponentId, findParentComponent])

	// Get all available components (for manual selection)
	const availableComponents: AvailableComponent[] = React.useMemo(() => {
		const components: AvailableComponent[] = []

		const collectComponents = (comps: typeof formComponents) => {
			comps.forEach((comp) => {
				// Don't include the average component itself
				if (
					comp.id !== selectedComponentId &&
					(comp.name === 'rating' || comp.name === 'number' || comp.name === 'select')
				) {
					components.push({
						id: comp.id,
						label: comp.label || (comp.custom as { label?: string })?.label || comp.name,
						name: comp.name,
					})
				}
				if (comp.children && comp.children.length > 0) {
					collectComponents(comp.children)
				}
			})
		}

		collectComponents(formComponents)
		return components
	}, [formComponents, selectedComponentId])

	// Track previous state to detect actual changes (not just re-renders)
	const prevAverageSectionRef = React.useRef(averageSection)
	const prevValueRef = React.useRef(value)

	// Auto-select section components when averageSection is enabled
	useEffect(() => {
		const currentIds = (value as string[]) || []

		// Case 1: averageSection just toggled ON (user clicked the toggle)
		const averageSectionToggledOn = averageSection && !prevAverageSectionRef.current

		// Case 2: averageSection is already on, but selectedComponentIds is empty
		// This happens when loading a saved component with averageSection: true but no IDs
		const needsInitialPopulation = averageSection && currentIds.length === 0

		if (averageSectionToggledOn || needsInitialPopulation) {
			const sectionComponents = getSectionComponents()
			const sectionComponentIds = sectionComponents.map((comp) => comp.id)

			// Only call onChange if there are actually components to select
			// AND the IDs are different from what we currently have
			const hasChanged =
				sectionComponentIds.length > 0 &&
				(sectionComponentIds.length !== currentIds.length || sectionComponentIds.some((id) => !currentIds.includes(id)))

			if (hasChanged) {
				onChange(sectionComponentIds)
			}
		}

		prevAverageSectionRef.current = averageSection
		prevValueRef.current = value
	}, [averageSection, getSectionComponents, value, onChange])

	const selectedIds = (value as string[]) || []

	const handleToggle = (id: string) => {
		const newSelectedIds = selectedIds.includes(id)
			? selectedIds.filter((selectedId) => selectedId !== id)
			: [...selectedIds, id]
		onChange(newSelectedIds)
	}

	// Show different UI based on averageSection toggle
	if (averageSection) {
		const sectionComponents = getSectionComponents()

		if (sectionComponents.length === 0) {
			return (
				<div key={name} className='space-y-2'>
					<Text variant='small' className='block mb-2 text-left'>
						{label}
					</Text>
					<div className='text-center py-4 text-neutral-500 border-2 border-dashed border-neutral-300 rounded-md'>
						<Text variant='small'>
							This average component must be placed inside a section to use "Average the Section"
						</Text>
					</div>
				</div>
			)
		}

		return (
			<div key={name} className='space-y-2'>
				<Text variant='small' className='block mb-2 text-left'>
					{label}
				</Text>
				<div className='space-y-2 max-h-60 overflow-y-auto border border-neutral-200 rounded-md p-3 bg-neutral-50'>
					<Text variant='xsmall' className='text-neutral-600 mb-2'>
						Components in this section (uncheck to exclude from average):
					</Text>
					{sectionComponents.map((comp) => (
						<div key={comp.id} className='flex items-center gap-2'>
							<input
								type='checkbox'
								id={`comp-${comp.id}`}
								checked={selectedIds.includes(comp.id)}
								onChange={() => handleToggle(comp.id)}
								className='w-4 h-4 rounded border-neutral-300 cursor-pointer'
							/>
							<label htmlFor={`comp-${comp.id}`} className='flex-1 cursor-pointer'>
								<Text variant='small' className='text-left text-neutral-600'>
									{comp.label}
									<span className='text-neutral-400 ml-2'>({comp.name})</span>
								</Text>
							</label>
						</div>
					))}
				</div>
			</div>
		)
	}

	return (
		<div key={name} className='space-y-2'>
			<Text variant='small' className='block mb-2 text-left'>
				{label}
			</Text>
			{availableComponents.length === 0 ? (
				<div className='text-center py-4 text-neutral-500 border-2 border-dashed border-neutral-300 rounded-md'>
					<Text variant='small'>No rating/number/select components available</Text>
				</div>
			) : (
				<div className='space-y-2 max-h-60 overflow-y-auto border border-neutral-200 rounded-md p-3'>
					{availableComponents.map((comp) => (
						<div key={comp.id} className='flex items-center gap-2'>
							<input
								type='checkbox'
								id={`comp-${comp.id}`}
								checked={selectedIds.includes(comp.id)}
								onChange={() => handleToggle(comp.id)}
								className='w-4 h-4 rounded border-neutral-300'
							/>
							<label htmlFor={`comp-${comp.id}`} className='cursor-pointer flex-1'>
								<Text variant='small' className='text-left'>
									{comp.label}
									<span className='text-neutral-400 ml-2'>({comp.name})</span>
								</Text>
							</label>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

class NineBoxConfigPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent(props: PropertyEditorProps): React.ReactElement {
		return <NineBoxConfigPropertyEditor {...props} />
	}
}

class NineBoxCellSizePropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent(props: PropertyEditorProps): React.ReactElement {
		return <NineBoxCellSizePropertyEditor {...props} />
	}
}

function NineBoxCellSizePropertyEditor({
	name,
	label,
	value,
	onChange,
}: Readonly<PropertyEditorProps>): React.ReactElement {
	// Guard against malformed legacy/imported values (number, string, array, …) before spreading,
	// so the next onChange doesn't discard whatever the original shape was.
	const isPlainObject = (v: unknown): v is Record<string, unknown> =>
		typeof v === 'object' && v !== null && !Array.isArray(v)
	const size: { width?: number; height?: number } = isPlainObject(value)
		? {
				width: typeof value.width === 'number' ? value.width : undefined,
				height: typeof value.height === 'number' ? value.height : undefined,
			}
		: {}

	const handleChange = (field: 'width' | 'height', raw: string) => {
		const trimmed = raw.trim()
		if (trimmed === '') {
			const next = { ...size }
			delete next[field]
			onChange(Object.keys(next).length === 0 ? undefined : next)
			return
		}
		// Reject mixed input (e.g. '5abc') instead of letting parseInt silently truncate to 5.
		if (!/^\d+$/.test(trimmed)) return
		const n = Number.parseInt(trimmed, 10)
		if (n <= 0) return
		onChange({ ...size, [field]: n })
	}

	return (
		<div key={name} className='space-y-3'>
			<Text variant='small' className='block text-left'>
				{label}
			</Text>
			<Text variant='xsmall' className='block text-left text-neutral-500'>
				Minimum cell size in pixels. Leave blank for auto-size. Cells always grow to fit longer labels.
			</Text>
			<div className='grid grid-cols-2 gap-2'>
				<div>
					<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
						Min Width (px)
					</Text>
					<Input
						type='number'
						min='1'
						value={size.width ?? ''}
						onChange={(e) => handleChange('width', e.target.value)}
						placeholder='auto'
					/>
				</div>
				<div>
					<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
						Min Height (px)
					</Text>
					<Input
						type='number'
						min='1'
						value={size.height ?? ''}
						onChange={(e) => handleChange('height', e.target.value)}
						placeholder='auto'
					/>
				</div>
			</div>
		</div>
	)
}

class RatingComponentSelectorStrategy implements PropertyEditorStrategy {
	getEditorComponent(props: PropertyEditorProps): React.ReactElement {
		return <RatingComponentSelectorEditor {...props} />
	}
}

interface BoxDefinition {
	label: string
	color: string
}

interface BoxDefinitions {
	[key: number]: BoxDefinition
}

function NineBoxConfigPropertyEditor({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
	const [selectedBox, setSelectedBox] = useState(1)

	const boxDefinitions = (value as BoxDefinitions) || {
		1: { label: 'Bad Hire', color: '#ef4444' },
		2: { label: 'Backup', color: '#ef4444' },
		3: { label: 'Workhorse', color: '#eab308' },
		4: { label: 'Inconsistent', color: '#ef4444' },
		5: { label: 'Core Player', color: '#eab308' },
		6: { label: 'High Performer', color: '#3b82f6' },
		7: { label: 'Potential Gem', color: '#eab308' },
		8: { label: 'High Potential', color: '#3b82f6' },
		9: { label: 'Star', color: '#3b82f6' },
	}

	const boxSelectOptions = Array.from({ length: 9 }, (_, i) => ({
		value: String(i + 1),
		label: `Box ${i + 1}`,
	}))

	const updateBoxDefinition = (boxNum: number, field: 'label' | 'color', newValue: string) => {
		const newBoxDefinitions = {
			...boxDefinitions,
			[boxNum]: {
				...boxDefinitions[boxNum],
				[field]: newValue,
			},
		}
		onChange(newBoxDefinitions)
	}

	const currentBox = boxDefinitions[selectedBox] || { label: '', color: '#000000' }

	return (
		<div key={name} className='space-y-4'>
			<Text variant='small' className='block mb-2 text-left'>
				{label}
			</Text>

			<div>
				<Text variant='xsmall' className='block mb-2 text-left text-neutral-600'>
					Select Box to Edit
				</Text>
				<Select
					value={String(selectedBox)}
					onChange={(val) => setSelectedBox(Number(val))}
					options={boxSelectOptions}
					placeholder='Select a box to edit'
				/>
			</div>

			<div className='space-y-3 p-3 border border-neutral-200 rounded-md bg-neutral-50'>
				<div>
					<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
						Box Label
					</Text>
					<Input
						value={currentBox.label}
						onChange={(e) => updateBoxDefinition(selectedBox, 'label', e.target.value)}
						placeholder='Enter box label'
					/>
				</div>

				<div>
					<Text variant='xsmall' className='block mb-1 text-left text-neutral-600'>
						Box Color
					</Text>
					<ColorPicker
						value={currentBox.color}
						onChange={(val) => updateBoxDefinition(selectedBox, 'color', val)}
						placeholder='#000000'
					/>
				</div>

				<div className='mt-2 p-2 rounded' style={{ backgroundColor: currentBox.color }}>
					<Text variant='xsmall' className='text-white text-center'>
						Preview: {currentBox.label}
					</Text>
				</div>
			</div>
		</div>
	)
}

class DatePickerPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
		const isoValue = value ? `${value}T00:00:00.000Z` : ''

		const handleChange = (isoDate: string) => {
			const dateOnly = isoDate ? isoDate.split('T')[0] : null
			onChange(dateOnly)
		}

		return (
			<div key={name}>
				<Text variant='small' className='block mb-2 text-left'>
					{label}
				</Text>
				<DatePicker value={isoValue} onChange={handleChange} placeholder='Select maximum date' allowPastDates={true} />
				{value && (
					<button
						type='button'
						onClick={() => onChange(null)}
						className='text-sm text-neutral-500 hover:text-neutral-700 mt-1'>
						Clear
					</button>
				)}
			</div>
		)
	}
}

class ImageUploadPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent(props: PropertyEditorProps): React.ReactElement {
		return <ImageUploadPropertyEditor {...props} />
	}
}

const MAX_IMAGE_SIZE_MB = 100
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024

type S3UploadFn = (input: {
	organizationId: string
	filename: string
	mimetype: FileUploadRequestInput['mimetype']
	size: number
}) => Promise<{ uploadUrl: string; readUrl: string | null; contentDisposition?: string | null }>

async function performPresignedS3Upload(
	file: File,
	uploadToS3: S3UploadFn,
	organizationId: string,
): Promise<{ readUrl: string | null }> {
	const data = await uploadToS3({
		organizationId,
		filename: file.name,
		mimetype: file.type as FileUploadRequestInput['mimetype'],
		size: file.size,
	})
	const { uploadUrl, readUrl, contentDisposition } = data
	const uploadResponse = await fetch(uploadUrl, {
		method: 'PUT',
		headers: {
			'Content-Type': file.type,
			...(contentDisposition ? { 'Content-Disposition': contentDisposition } : {}),
		},
		body: file,
	})
	if (!uploadResponse.ok) {
		throw new Error('Upload failed')
	}
	return { readUrl }
}

function ImageUploadPropertyEditor({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
	const { organizationId } = useParams<{ organizationId: string }>()
	const [isUploading, setIsUploading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const beginUpload = useFormBuilderStore((state) => state.beginUpload)
	const endUpload = useFormBuilderStore((state) => state.endUpload)

	const { mutateAsync: uploadToS3 } = useMutation({
		...trpc.admin.organizations.logoUploadRequest.mutationOptions(),
	})

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const fileInput = e.target
		const file = fileInput.files?.[0]
		// Reset value so the user can re-select the same file after an error/clear.
		fileInput.value = ''
		if (!file) return

		// Validate file type
		if (!file.type.startsWith('image/')) {
			setError('Please select an image file')
			return
		}

		// Validate file size (100MB max)
		if (file.size > MAX_IMAGE_SIZE_BYTES) {
			setError(`File size must not exceed ${MAX_IMAGE_SIZE_MB}MB`)
			return
		}

		setError(null)
		setIsUploading(true)
		beginUpload()

		try {
			// Local image/* check above is inside server-side allow-list (Wrike 4409828829 / MA1.1).
			const { readUrl } = await performPresignedS3Upload(file, uploadToS3, organizationId as string)
			onChange(readUrl ?? '')
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to upload image')
		} finally {
			setIsUploading(false)
			endUpload()
		}
	}

	const handleClear = () => {
		onChange('')
		setError(null)
	}

	const [showUrlInput, setShowUrlInput] = useState(false)
	const [urlInputValue, setUrlInputValue] = useState('')

	const handleUrlSubmit = () => {
		if (urlInputValue.trim()) {
			onChange(urlInputValue.trim())
			setUrlInputValue('')
			setShowUrlInput(false)
		}
	}

	return (
		<div key={name} className='space-y-2'>
			<Text variant='small' className='block mb-2 text-left'>
				{label}
			</Text>

			{isUploading ? (
				<div className='flex items-center justify-center h-32 bg-neutral-100 rounded-md border border-neutral-200'>
					<div className='flex flex-col items-center gap-2'>
						<div className='animate-spin inline-block size-6 border-2 border-current border-t-transparent text-primary-600 rounded-full' />
						<Text variant='xsmall' className='text-neutral-500'>
							Uploading...
						</Text>
					</div>
				</div>
			) : value ? (
				<div className='space-y-2'>
					<div className='relative'>
						<img
							src={value as string}
							alt='Uploaded content'
							className='w-full h-auto max-h-48 object-contain rounded-md border border-neutral-200'
						/>
					</div>
					<Button variant='tertiary' size='small' onClick={handleClear}>
						Remove Image
					</Button>
				</div>
			) : showUrlInput ? (
				<div className='flex flex-col gap-2'>
					<Input
						value={urlInputValue}
						onChange={(e) => setUrlInputValue(e.target.value)}
						placeholder='Paste image URL...'
					/>
					<div className='flex gap-2'>
						<Button variant='primary' size='small' onClick={handleUrlSubmit}>
							Use URL
						</Button>
						<Button variant='tertiary' size='small' onClick={() => setShowUrlInput(false)}>
							Cancel
						</Button>
					</div>
				</div>
			) : (
				<div className='flex flex-col gap-2'>
					<label className='flex flex-col items-center justify-center h-32 bg-neutral-50 border-2 border-dashed border-neutral-300 rounded-md cursor-pointer hover:bg-neutral-100 transition-colors'>
						<div className='flex flex-col items-center'>
							<Text variant='small' className='text-neutral-500'>
								Click to upload image
							</Text>
							<Text variant='xsmall' className='text-neutral-400 mt-1'>
								Max {MAX_IMAGE_SIZE_MB}MB
							</Text>
						</div>
						<input type='file' accept='image/*' onChange={handleFileChange} className='hidden' />
					</label>
					<button
						type='button'
						onClick={() => setShowUrlInput(true)}
						className='text-sm text-primary-600 hover:text-primary-700 underline'>
						Or paste image URL
					</button>
				</div>
			)}

			{error && (
				<Text variant='xsmall' className='text-error-600'>
					{error}
				</Text>
			)}
		</div>
	)
}

class DocumentUploadPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent(props: PropertyEditorProps): React.ReactElement {
		return <DocumentUploadPropertyEditor {...props} />
	}
}

const ALLOWED_DOC_MIME_TYPES = new Set([
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
])
const MAX_DOC_SIZE_MB = 100
const MAX_DOC_SIZE_BYTES = MAX_DOC_SIZE_MB * 1024 * 1024

interface DocumentValue {
	url: string
	filename: string
	mimeType: string
}

function DocumentUploadPropertyEditor({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
	const { organizationId } = useParams<{ organizationId: string }>()
	const [isUploading, setIsUploading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [isLibraryOpen, setIsLibraryOpen] = useState(false)
	const inputId = React.useId()
	const beginUpload = useFormBuilderStore((state) => state.beginUpload)
	const endUpload = useFormBuilderStore((state) => state.endUpload)

	const { mutateAsync: uploadToS3 } = useMutation({
		...trpc.admin.organizations.logoUploadRequest.mutationOptions(),
	})

	const uploadedDoc = (value as DocumentValue | undefined) ?? undefined

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const fileInput = e.target
		const file = fileInput.files?.[0]
		// Reset so the same file can be re-selected after an error or removal.
		fileInput.value = ''
		if (!file) return

		if (!ALLOWED_DOC_MIME_TYPES.has(file.type)) {
			setError('Only PDF, DOC, and DOCX files are allowed')
			return
		}

		if (file.size > MAX_DOC_SIZE_BYTES) {
			setError(`File size must not exceed ${MAX_DOC_SIZE_MB}MB`)
			return
		}

		if (!organizationId) {
			setError('Missing organization context — reload the page and try again')
			return
		}

		setError(null)
		setIsUploading(true)
		beginUpload()

		// Async work fired from a sync DOM handler — explicit IIFE so the
		// floating promise is contained (rather than leaking up through the
		// React event handler return type) and any error path always reaches
		// `setIsUploading(false)` + `endUpload()`.
		void (async () => {
			try {
				// `ALLOWED_DOC_MIME_TYPES.has(file.type)` above guarantees server-side allow-list compliance.
				// performPresignedS3Upload echoes Content-Disposition (returned by API for documents) back
				// in the PUT, otherwise S3 returns SignatureDoesNotMatch.
				const { readUrl } = await performPresignedS3Upload(file, uploadToS3, organizationId)
				if (!readUrl) {
					throw new Error('Upload did not return a read URL')
				}
				onChange({ url: readUrl, filename: file.name, mimeType: file.type })
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to upload document')
			} finally {
				setIsUploading(false)
				endUpload()
			}
		})()
	}

	const handleClear = () => {
		onChange(undefined)
		setError(null)
	}

	const handleSelectFromLibrary = (doc: { url: string; filename: string; mimeType: string }) => {
		onChange(doc)
		setError(null)
		setIsLibraryOpen(false)
	}

	const docState: 'uploading' | 'uploaded' | 'empty' = isUploading
		? 'uploading'
		: uploadedDoc?.url
			? 'uploaded'
			: 'empty'

	return (
		<div key={name} className='space-y-2'>
			<Text variant='small' className='block mb-2 text-left'>
				{label}
			</Text>
			{docState === 'uploading' && (
				<output aria-live='polite' className='block text-xs text-neutral-500'>
					Uploading document...
				</output>
			)}
			{docState === 'uploaded' && uploadedDoc && (
				<div
					className='flex items-center justify-between gap-2 p-2 border border-neutral-200 rounded-md bg-neutral-50'
					data-testid='document-uploaded-row'>
					<Text variant='small' className='truncate flex-1 min-w-0' title={uploadedDoc.filename || 'Uploaded document'}>
						{uploadedDoc.filename || 'Uploaded document'}
					</Text>
					<Button variant='tertiary' size='small' onClick={handleClear}>
						Remove
					</Button>
				</div>
			)}
			{docState === 'empty' && (
				<div className='flex flex-col gap-2'>
					<label htmlFor={inputId} className='block text-sm text-primary-600 underline cursor-pointer'>
						Click to upload PDF / DOC / DOCX (max {MAX_DOC_SIZE_MB}MB)
						<input
							id={inputId}
							type='file'
							aria-label='Upload PDF, DOC, or DOCX document'
							accept='.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
							onChange={handleFileChange}
							className='hidden'
							data-testid='document-upload-input'
						/>
					</label>
					<button
						type='button'
						onClick={() => setIsLibraryOpen(true)}
						className='block text-sm text-primary-600 underline text-left cursor-pointer'
						data-testid='document-library-trigger'>
						Or choose from library
					</button>
				</div>
			)}
			{error && (
				<Text variant='xsmall' role='alert' className='text-error-600'>
					{error}
				</Text>
			)}
			<DocumentLibraryModal
				isOpen={isLibraryOpen}
				onClose={() => setIsLibraryOpen(false)}
				onSelect={handleSelectFromLibrary}
				organizationId={organizationId}
			/>
		</div>
	)
}

// Inferred from the tRPC router so a backend rename of any field is caught at
// compile time. Matches the codebase pattern in apps/admin/src/modules/dealteam/*.
type RouterOutput = inferRouterOutputs<AppRouter>
type OrganizationDocument = RouterOutput['admin']['organizations']['listDocuments']['documents'][number]

interface DocumentLibraryModalProps {
	readonly isOpen: boolean
	readonly onClose: () => void
	readonly onSelect: (doc: { url: string; filename: string; mimeType: string }) => void
	readonly organizationId: string | undefined
}

function DocumentLibraryModal({ isOpen, onClose, onSelect, organizationId }: DocumentLibraryModalProps) {
	// Only fetch when the modal is open AND we know the org — avoids a useless
	// query when the page first mounts and prevents leaking the request when
	// the editor renders for an org-less route during navigation.
	const enabled = isOpen && Boolean(organizationId)
	// staleTime: 0 ensures every modal-open fetches fresh data so a CSM who
	// uploaded a new document on a different form within the same session sees
	// it immediately when picking from the library on another component.
	const { data, isLoading, isError, refetch } = useQuery({
		...trpc.admin.organizations.listDocuments.queryOptions({ organizationId: organizationId ?? '' }),
		enabled,
		staleTime: 0,
	})

	const documents: OrganizationDocument[] = data?.documents ?? []

	return (
		<Modal isOpen={isOpen} onClose={onClose} title='Choose document from library' bodyClassName='p-0'>
			<div className='p-4 max-h-[60vh] overflow-y-auto' data-testid='document-library-modal-body'>
				{isLoading && (
					<Text variant='small' className='text-neutral-500'>
						Loading library...
					</Text>
				)}
				{isError && (
					<div className='space-y-2'>
						<Text variant='small' role='alert' className='text-error-600'>
							Failed to load documents.
						</Text>
						<Button
							variant='tertiary'
							size='small'
							onClick={() => {
								refetch().catch(() => {
									// Errors are surfaced via the `isError` branch above.
								})
							}}>
							Retry
						</Button>
					</div>
				)}
				{!isLoading && !isError && documents.length === 0 && (
					<Text variant='small' className='text-neutral-500' data-testid='document-library-empty'>
						No documents uploaded yet for this organization.
					</Text>
				)}
				{!isLoading && !isError && documents.length > 0 && (
					<ul className='divide-y divide-neutral-200' data-testid='document-library-list'>
						{documents.map((doc) => (
							<li key={`${doc.url}-${doc.filename}`}>
								<button
									type='button'
									onClick={() => onSelect({ url: doc.url, filename: doc.filename, mimeType: doc.mimeType })}
									className='w-full flex items-center justify-between gap-3 px-2 py-3 text-left hover:bg-neutral-50 cursor-pointer'
									data-testid='document-library-item'>
									<div className='min-w-0 flex-1'>
										<Text variant='small' className='block truncate' title={doc.filename}>
											{doc.filename}
										</Text>
										{doc.formName && (
											<Text variant='xsmall' className='text-neutral-500 truncate'>
												Used in {doc.formName}
											</Text>
										)}
									</div>
									<Text variant='xsmall' className='text-neutral-400 shrink-0'>
										{describeMimeType(doc.mimeType)}
									</Text>
								</button>
							</li>
						))}
					</ul>
				)}
			</div>
		</Modal>
	)
}

function describeMimeType(mimeType: string): string {
	switch (mimeType) {
		case 'application/pdf':
			return 'PDF'
		case 'application/msword':
			return 'DOC'
		case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
			return 'DOCX'
		default:
			return mimeType
	}
}

class ConditionalConfigPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent(props: PropertyEditorProps): React.ReactElement {
		return <ConditionalConfigEditor {...props} />
	}
}

class DataSourceConfigPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent(props: PropertyEditorProps): React.ReactElement {
		return <DataSourceConfigEditor {...props} />
	}
}

class ExternalDataSelectorPropertyEditorStrategy implements PropertyEditorStrategy {
	getEditorComponent(props: PropertyEditorProps): React.ReactElement {
		return <ExternalDataSourceSelectorEditor {...props} />
	}
}

const inputComponentNames = new Set(
	componentRegistry.filter((entry) => entry.type === 'input').map((entry) => entry.name),
)

function collectInputComponents(
	formComponents: FormInputComponent[],
	excludeId: string | null,
): { id: string; label: string; componentName: string }[] {
	const components: { id: string; label: string; componentName: string }[] = []
	const collect = (comps: FormInputComponent[]) => {
		for (const comp of comps) {
			if (comp.id !== excludeId && inputComponentNames.has(comp.name)) {
				components.push({
					id: comp.id,
					label: comp.label || (comp.custom as { label?: string })?.label || comp.name,
					componentName: comp.name,
				})
			}
			if (comp.children?.length) collect(comp.children)
		}
	}
	collect(formComponents)
	return components
}

function AggregationSourceSelectorEditor({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
	const formComponents = useFormBuilderStore((state) => state.formComponents)
	const currentComponentId = useFormBuilderStore((state) => state.selectedComponentId)

	const availableComponents = React.useMemo(
		() => collectInputComponents(formComponents, currentComponentId),
		[formComponents, currentComponentId],
	)

	const selectedIds = (value as string[]) || []

	const rowKeysRef = React.useRef<string[]>([])
	while (rowKeysRef.current.length < selectedIds.length) rowKeysRef.current.push(crypto.randomUUID())

	const addSource = () => {
		rowKeysRef.current.push(crypto.randomUUID())
		onChange([...selectedIds, ''])
	}
	const removeSource = (index: number) => {
		rowKeysRef.current.splice(index, 1)
		onChange(selectedIds.filter((_, i) => i !== index))
	}
	const updateSource = (index: number, id: string) => {
		const updated = [...selectedIds]
		updated[index] = id
		onChange(updated)
	}

	const lockedComponentName = React.useMemo(() => {
		for (const id of selectedIds) {
			if (id) {
				const comp = availableComponents.find((c) => c.id === id)
				if (comp) return comp.componentName
			}
		}
		return null
	}, [selectedIds, availableComponents])

	const lockedDisplayName = React.useMemo(() => {
		if (!lockedComponentName) return null
		return componentRegistry.find((r) => r.name === lockedComponentName)?.displayName ?? lockedComponentName
	}, [lockedComponentName])

	return (
		<div key={name} className='space-y-3'>
			<div>
				<Text variant='small' weight='semibold' className='block mb-1 text-left'>
					{label}
				</Text>
				<Text variant='xsmall' className='text-neutral-500 text-left'>
					Select sources of the same component type to aggregate into a single result.
				</Text>
			</div>

			{lockedDisplayName && (
				<Text variant='xsmall' className='text-neutral-500 text-left'>
					Showing only <strong>{lockedDisplayName}</strong> components.
				</Text>
			)}

			{selectedIds.map((selectedId, index) => {
				const otherSelected = selectedIds.filter((_, i) => i !== index)
				const options = availableComponents
					.filter((c) => !otherSelected.includes(c.id))
					.filter((c) => !lockedComponentName || c.componentName === lockedComponentName)
					.map((c) => ({ value: c.id, label: c.label }))
				return (
					<div key={rowKeysRef.current[index]} className='flex gap-2 items-center'>
						<div className='flex-1'>
							<Select
								value={selectedId}
								onChange={(val) => updateSource(index, val)}
								options={options}
								placeholder='Select component'
							/>
						</div>
						<button
							type='button'
							onClick={() => removeSource(index)}
							className='shrink-0 text-neutral-400 hover:text-red-500 transition-colors'>
							<DeleteIcon size={16} />
						</button>
					</div>
				)
			})}

			<Button onClick={addSource} variant='secondary' size='small' className='w-full'>
				+ Add Source
			</Button>
		</div>
	)
}

class AggregationSourceSelectorStrategy implements PropertyEditorStrategy {
	getEditorComponent(props: PropertyEditorProps): React.ReactElement {
		return <AggregationSourceSelectorEditor {...props} />
	}
}

const OPTION_TYPE_AGGREGATIONS = [
	{ value: 'distribution', label: 'Distribution' },
	{ value: 'mostVoted', label: 'Most Voted Option' },
	{ value: 'leastVoted', label: 'Least Voted Option' },
]

const NUMERIC_TYPE_AGGREGATIONS = [
	{ value: 'distribution', label: 'Distribution' },
	{ value: 'average', label: 'Average' },
	{ value: 'max', label: 'Maximum' },
	{ value: 'minimum', label: 'Minimum' },
]

const AGGREGATION_OPTIONS_BY_SOURCE_TYPE: Record<string, Array<{ value: string; label: string }>> = {
	text: [
		{ value: 'combine', label: 'Combine' },
		{ value: 'groupByRole', label: 'Group by Role' },
	],
	textarea: [
		{ value: 'combine', label: 'Combine' },
		{ value: 'groupByRole', label: 'Group by Role' },
	],
	checkbox: OPTION_TYPE_AGGREGATIONS,
	'radio-group': OPTION_TYPE_AGGREGATIONS,
	select: OPTION_TYPE_AGGREGATIONS,
	'9boxrating': OPTION_TYPE_AGGREGATIONS,
	rating: NUMERIC_TYPE_AGGREGATIONS,
	number: NUMERIC_TYPE_AGGREGATIONS,
}

function findById(comps: FormInputComponent[], id: string | null): FormInputComponent | null {
	if (!id) return null
	for (const comp of comps) {
		if (comp.id === id) return comp
		if (comp.children?.length) {
			const found = findById(comp.children as FormInputComponent[], id)
			if (found) return found
		}
	}
	return null
}

function AggregationTypeSelectorEditor({ name, label, value, onChange }: PropertyEditorProps): React.ReactElement {
	const formComponents = useFormBuilderStore((state) => state.formComponents)
	const currentComponentId = useFormBuilderStore((state) => state.selectedComponentId)

	const lockedComponentName = React.useMemo(() => {
		const aggComp = findById(formComponents, currentComponentId)
		const selectedIds = (aggComp?.custom as { selectedComponentIds?: string[] })?.selectedComponentIds ?? []
		for (const id of selectedIds) {
			if (id) return findById(formComponents, id)?.name ?? null
		}
		return null
	}, [formComponents, currentComponentId])

	const aggregationOptions = lockedComponentName ? (AGGREGATION_OPTIONS_BY_SOURCE_TYPE[lockedComponentName] ?? []) : []

	React.useEffect(() => {
		if (value && !aggregationOptions.find((o) => o.value === value)) {
			onChange('')
		}
	}, [aggregationOptions, value, onChange])

	return (
		<div key={name} className='space-y-3'>
			<div>
				<Text variant='small' weight='semibold' className='block mb-2 text-left'>
					{label}
				</Text>
				{aggregationOptions.length > 0 ? (
					<Select
						value={(value as string) || ''}
						placeholder='Select aggregation type...'
						onChange={(val) => onChange(val)}
						options={aggregationOptions}
					/>
				) : (
					<Text variant='xsmall' className='text-neutral-400 text-left'>
						{lockedComponentName === 'date'
							? 'Date inputs are not supported as an aggregation source.'
							: lockedComponentName
								? 'No aggregation options available for this component type yet.'
								: 'Select a source first to see aggregation options.'}
					</Text>
				)}
			</div>
			<div className='flex gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md'>
				<LockIcon size={16} className='shrink-0 text-blue-600 mt-0.5' />
				<Text variant='xsmall' className='text-blue-800 text-left'>
					<strong>Anonymity Enforced:</strong> To protect respondent privacy, raw individual data is hidden. Only
					aggregated results will be visible.
				</Text>
			</div>
		</div>
	)
}

class AggregationTypeSelectorStrategy implements PropertyEditorStrategy {
	getEditorComponent(props: PropertyEditorProps): React.ReactElement {
		return <AggregationTypeSelectorEditor {...props} />
	}
}

export const propertyEditorStrategies: PropertyEditorStrategyMap = {
	text: new TextPropertyEditorStrategy(),
	color: new ColorPropertyEditorStrategy(),
	select: new SelectPropertyEditorStrategy(),
	number: new NumberPropertyEditorStrategy(),
	boolean: new BooleanPropertyEditorStrategy(),
	ratingConfig: new RatingPropertyEditorStrategy(),
	divider: new DividerPropertyEditorStrategy(),
	textarea: new TextAreaPropertyEditorStrategy(),
	richtextarea: new RichTextAreaPropertyEditorStrategy(),
	options: new OptionsPropertyEditorStrategy(),
	textLimit: new TextLimitPropertyEditorStrategy(),
	nineBoxConfig: new NineBoxConfigPropertyEditorStrategy(),
	nineBoxCellSize: new NineBoxCellSizePropertyEditorStrategy(),
	ratingComponentSelector: new RatingComponentSelectorStrategy(),
	aggregationSourceSelector: new AggregationSourceSelectorStrategy(),
	aggregationTypeSelector: new AggregationTypeSelectorStrategy(),
	datePicker: new DatePickerPropertyEditorStrategy(),
	imageUpload: new ImageUploadPropertyEditorStrategy(),
	documentUpload: new DocumentUploadPropertyEditorStrategy(),
	defaultValueSelector: new DefaultValueSelectorPropertyEditorStrategy(),
	conditionalConfig: new ConditionalConfigPropertyEditorStrategy(),
	dataSourceConfig: new DataSourceConfigPropertyEditorStrategy(),
	externalDataSelector: new ExternalDataSelectorPropertyEditorStrategy(),
}

export const getPropertyEditorStrategy = (type: string): PropertyEditorStrategy => {
	return propertyEditorStrategies[type] || propertyEditorStrategies.text
}
